import { EntityList } from 'src/types/common-types';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Cards from 'src/components/form/search/cards/cards';
import PageHeader from 'src/components/page-header/page-header';
import PaginatedFooter from 'src/components/paginated-footer/paginated-footer';
import { fireEvent } from 'src/utils/analytics/google';
import API from './api/api';
import Course from './course';
import css from './courses.module.scss';
import EmptyPlaceholder from './empty-placeholder';
import SearchForm from './form/form';
import { CourseFilters, CourseItem } from './types';

export default function Education() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams?.get('search');
  const vendor = searchParams?.get('vendor');
  const skeletons = [...Array(6).keys()].map((i) => <span key={i} className={css.skeleton} />);
  const [filterValues, setFilterValues] = useState({ search: searchValue ?? '', vendor: vendor ?? '' });
  const [selectedPage, setSelectedPage] = useState(1);
  const { courses, isLoading } = useCourses(filterValues, selectedPage);

  const hidePaginatedFooter =
    courses.items.length === 0 || courses.totalCount === 0 || courses.items.length === courses.totalCount;

  function handleFilterValuesChange(data: Partial<CourseFilters>) {
    const nextSearchParams = new URLSearchParams(searchParams);
    const trimmedText = data.search?.trim();
    trimmedText && trimmedText.length > 0
      ? nextSearchParams.set('search', trimmedText)
      : nextSearchParams.delete('search');
    data.vendor && data.vendor?.length > 0
      ? nextSearchParams.set('vendor', data.vendor?.trim())
      : nextSearchParams.delete('vendor');
    if (nextSearchParams.toString() !== searchParams.toString()) setSearchParams(nextSearchParams);
    setFilterValues({ ...filterValues, ...data, search: trimmedText ?? '' });
    setSelectedPage(1);
  }

  return (
    <main className={css.courses}>
      <PageHeader
        title='Каталог курсів'
        description='Вивчайте цивільне та військове, щоб бути унікальним'
        filter={
          <div className={css.filters}>
            <SearchForm initialValues={filterValues} onSubmit={handleFilterValuesChange} />
          </div>
        }
      />
      <div className={css.body}>
        <Cards className={css.list} loading={isLoading}>
          {!isLoading && courses.items.length === 0 && <EmptyPlaceholder />}
          {courses.items.map((course) => (
            <Course key={course.id} course={course} />
          ))}
          {isLoading && skeletons}
          {!hidePaginatedFooter && (
            <div className={css.pagerRow}>
              <PaginatedFooter caption='Більше курсів' onClick={() => setSelectedPage(selectedPage + 1)} />
            </div>
          )}
        </Cards>
      </div>
    </main>
  );
}

function useCourses(filters: CourseFilters, selectedPage: number) {
  const [courses, setCourses] = useState<EntityList<CourseItem>>({
    totalCount: 0,
    items: []
  });
  const [isLoading, setIsLoading] = useState(true);

  const getIds = (items: CourseItem[]) => new Set(items.map((v) => v.id));
  const filteredItems = (items: CourseItem[], ids: Set<string>) => items.filter((v) => !ids.has(v.id));

  useEffect(() => {
    setIsLoading(true);
    fireEvent('education_form_submit', { search: filters.search });
    API.search(selectedPage, { search: filters.search, vendor: filters.vendor })
      .then((response) => {
        setCourses((prev) => {
          if (selectedPage > 1) {
            const ids = getIds(prev.items);
            return {
              totalCount: response.totalCount,
              items: [...prev.items, ...filteredItems(response.items, ids)]
            };
          }

          return response;
        });
      })
      .finally(() => setIsLoading(false));
  }, [filters, selectedPage]);

  return { courses, isLoading };
}
